/*

& Create Property drafts for the website wizard (CreatePropertyWithUploadTool).

  The same drafts the app keeps (lib/listings/propertyDraft.ts): a `properties` row with
  `status: "draft"`, saved and read in the app wizard's body shape (property_name,
  property_location_coordinates, ...). The website's routes
  (pages/api/business-dashboard/listings/{save,fetch,discard}-property-draft) take and
  answer that same shape, so a draft started on either side opens on the other.

  This file only translates between that shape and the website wizard's form state
  (`new_property_data`), and calls the three routes. Client-safe: no models.

  The website form keeps an area as the number the user typed plus its unit, in a fixed
  slot: built-up and carpet area in `.ft`, land area in `max.acre` (see the
  setCreateNewProperty*Area reducers and createNewPropertyWithUploadToolThunk). Its
  possession date is the date picker's Date (older form code kept "DD/MM/YYYY"); a draft
  carries "YYYY-MM-DD".

*/

import StateInterface from "../../redux-magic/state-interface";
import { new_property_data as emptyNewPropertyData } from "../../redux-magic/sub-state-data/new-property-data";

type NewPropertyData = StateInterface["new_property_data"];

/** The app wizard's body, as fetch-property-draft answers it (draftToWizardBody). */
export type WizardDraftBody = Record<string, any>;

/** A finite number, or 0. */
function num(value: unknown): number {
	const n = Number(value);
	return Number.isFinite(n) ? n : 0;
}

/** The non-blank strings of an array, or []. */
function strArray(value: unknown): string[] {
	return Array.isArray(value) ? value.filter((v) => typeof v === "string" && v.trim() !== "") : [];
}

/**
 * The website form's possession date (the picker's Date, or an older "DD/MM/YYYY" or date
 * string) to "YYYY-MM-DD" (a draft), or "". The day is read in local time, the day the
 * user picked.
 */
function toIsoDate(value: unknown): string {
	if (typeof value === "string" && value.split("/").length === 3) {
		const [day, month, year] = value.split("/");
		return `${year}-${month.padStart(2, "0")}-${day.padStart(2, "0")}`;
	}
	const date = value instanceof Date ? value : typeof value === "string" && value !== "" ? new Date(value) : null;
	if (!date || isNaN(date.getTime())) return "";
	const month = String(date.getMonth() + 1).padStart(2, "0");
	const day = String(date.getDate()).padStart(2, "0");
	return `${date.getFullYear()}-${month}-${day}`;
}

/** "YYYY-MM-DD" (a draft) to the Date the form's picker holds (local midnight), or "". */
function fromIsoDate(value: unknown): Date | string {
	const parts = typeof value === "string" ? value.split("-").map(Number) : [];
	if (parts.length !== 3 || parts.some((part) => !Number.isFinite(part))) return "";
	const [year, month, day] = parts;
	return new Date(year, month - 1, day);
}

/**
 * The website form as a save-property-draft body. Photo captions are left out: the form
 * keeps a preview URL there, which is no caption.
 */
export function newPropertyDataToDraftBody(
	data: NewPropertyData,
	propertyTitle: string,
	businessProfileId: string,
	draftPropertyId: string,
): WizardDraftBody {
	const coordinates = data.search_metrics?.location_coordinates || [];
	/* [1, 1] is the form's "no location picked yet" placeholder. */
	const hasLocation = coordinates.length === 2 && !(coordinates[0] === 1 && coordinates[1] === 1);

	return {
		business_profile_id: businessProfileId,
		draft_property_id: draftPropertyId,
		property_name: propertyTitle,
		property_location_name: data.location?.title || "",
		property_location_coordinates: hasLocation ? coordinates : [],
		property_country: data.location?.country || "",
		property_state_or_province: data.location?.state_or_province || "",
		property_city: data.location?.city || "",
		property_transaction_type: data.transaction_type || "",
		property_category: data.property_category || "",
		property_type: strArray(data.property_type),
		property_currency: data.price?.currency || "INR",
		property_min_price: num(data.price?.min),
		property_max_price: num(data.price?.max),
		property_security_deposit: num(data.security_deposit?.max),
		property_status: strArray(data.availability),
		property_possession_date: toIsoDate(data.possession_date),
		property_age: num(data.property_age),
		property_facing: strArray(data.unit_facing),
		property_furnishing: strArray(data.furnished),
		property_min_bedrooms: num(data.bhk?.min),
		property_max_bedrooms: num(data.bhk?.max),
		property_min_bathrooms: num(data.bathroom?.min),
		property_max_bathrooms: num(data.bathroom?.max),
		property_no_of_balconies: num(data.balconies?.max),
		property_no_of_floors: num(data.floors?.max),
		property_floor_no: num(data.floor_no?.max),
		property_no_of_seats: num(data.no_of_seats?.max),
		property_no_of_units: num(data.no_of_units?.max),
		property_plot_dimensions: strArray(data.plot_dimensions),
		property_land_area: num(data.area?.land_area?.max?.acre),
		property_land_area_unit: data.area?.land_area?.land_area_unit || "acre",
		property_super_built_up_area_unit: data.area?.super_built_up_area?.super_built_up_area_unit || "sqft",
		property_min_super_built_up_area: num(data.area?.super_built_up_area?.min?.ft),
		property_max_super_built_up_area: num(data.area?.super_built_up_area?.max?.ft),
		property_is_carpet_area_percentage: false,
		property_carpet_area_unit: data.area?.carpet_area?.carpet_area_unit || "sqft",
		property_min_carpet_area: num(data.area?.carpet_area?.min?.ft),
		property_max_carpet_area: num(data.area?.carpet_area?.max?.ft),
		property_overview: data.description || "",
		property_regulatory_permit: {
			permit_number: data.regulatory_permit?.permit_number || "",
			issuing_authority: data.regulatory_permit?.issuing_authority || "",
		},
		broker_license: {
			permit_number: data.broker_license?.permit_number || "",
			permit_name: data.broker_license?.permit_name || "",
		},
		property_images: (data.images || []).map((image, index) => ({
			file_id: String(image.file_id),
			mime_type: image.mime_type || "",
			category: image.category || "exterior",
			caption: "",
			priority: Number.isFinite(Number(image.priority)) ? Number(image.priority) : index,
		})),
	};
}

/** A draft read back into the website form, on top of the form's empty state. */
export function draftToNewPropertyData(draft: WizardDraftBody): NewPropertyData {
	const data: NewPropertyData = JSON.parse(JSON.stringify(emptyNewPropertyData));
	const coordinates = Array.isArray(draft.property_location_coordinates) ? draft.property_location_coordinates : [];
	const sbaUnit = draft.property_super_built_up_area_unit || "sqft";
	const range = (min: number, max: number) => ({ min, max, label: "", enableLabel: false });

	data.title = draft.property_name || "";
	data.location = {
		...data.location,
		title: draft.property_location_name || "",
		country: draft.property_country || "",
		state_or_province: draft.property_state_or_province || "",
		city: draft.property_city || "",
	};
	data.search_metrics.location_coordinates = coordinates.length === 2 ? coordinates.map(num) : [1, 1];
	data.transaction_type = draft.property_transaction_type || "";
	data.property_category = draft.property_category || "";
	data.property_type = strArray(draft.property_type);
	data.price = { ...data.price, min: num(draft.property_min_price), max: num(draft.property_max_price) };
	data.price.currency = draft.property_currency === "AED" ? "AED" : "INR";
	data.security_deposit = {
		...data.security_deposit,
		min: num(draft.property_security_deposit),
		max: num(draft.property_security_deposit),
		currency: data.price.currency,
	};
	data.availability = strArray(draft.property_status);
	data.possession_date = fromIsoDate(draft.property_possession_date);
	data.property_age = num(draft.property_age);
	data.unit_facing = strArray(draft.property_facing);
	data.furnished = strArray(draft.property_furnishing);
	data.bhk = range(num(draft.property_min_bedrooms), num(draft.property_max_bedrooms));
	data.bathroom = range(num(draft.property_min_bathrooms), num(draft.property_max_bathrooms));
	data.balconies = range(num(draft.property_no_of_balconies), num(draft.property_no_of_balconies));
	data.floors = range(num(draft.property_no_of_floors), num(draft.property_no_of_floors));
	data.floor_no = range(num(draft.property_floor_no), num(draft.property_floor_no));
	data.no_of_seats = range(num(draft.property_no_of_seats), num(draft.property_no_of_seats));
	data.no_of_units = range(num(draft.property_no_of_units), num(draft.property_no_of_units));
	data.plot_dimensions = strArray(draft.property_plot_dimensions);

	data.area.super_built_up_area.super_built_up_area_unit = sbaUnit;
	data.area.super_built_up_area.min.ft = num(draft.property_min_super_built_up_area);
	data.area.super_built_up_area.max.ft = num(draft.property_max_super_built_up_area);
	data.area.land_area.land_area_unit = draft.property_land_area_unit || "acre";
	data.area.land_area.max.acre = num(draft.property_land_area);
	/* The website form has no "% of built-up" carpet entry, so a percentage reads as empty. */
	if (draft.property_is_carpet_area_percentage === false) {
		data.area.carpet_area.carpet_area_unit = draft.property_carpet_area_unit || "sqft";
		data.area.carpet_area.min.ft = num(draft.property_min_carpet_area);
		data.area.carpet_area.max.ft = num(draft.property_max_carpet_area);
	}

	data.description = draft.property_overview || "";
	data.regulatory_permit = {
		permit_number: draft.property_regulatory_permit?.permit_number || "",
		issuing_authority: draft.property_regulatory_permit?.issuing_authority || "",
	};
	data.broker_license = {
		permit_number: draft.broker_license?.permit_number || "",
		permit_name: draft.broker_license?.permit_name || "",
	};

	/* The photo step shows each photo from `caption`, so the presigned URL goes there. */
	const images = Array.isArray(draft.property_images) ? draft.property_images : [];
	data.images = images.map((image: any, index: number) => ({
		_id: "",
		file_id: String(image.file_id),
		mime_type: image.mime_type || "",
		category: image.category || "exterior",
		caption: image.url || "",
		priority: index,
		original_file_server_path: "",
		stage: "FETCHED_FILE_INFORMATION",
	}));
	data.image_upload_completed = data.images.length > 0;
	data.is_ofb = false;

	return data;
}

/**
 * The first wizard step a draft still needs: 1 (location), 2 (details), else 3, from
 * where Next walks through what is already filled in.
 */
export function draftResumeStep(draft: WizardDraftBody): number {
	const coordinates = draft.property_location_coordinates;
	if (!Array.isArray(coordinates) || coordinates.length !== 2 || !draft.property_location_name) return 1;
	if (
		!draft.property_transaction_type ||
		!draft.property_category ||
		strArray(draft.property_type).length === 0 ||
		!num(draft.property_min_price) ||
		!num(draft.property_max_price) ||
		strArray(draft.property_status).length === 0
	) {
		return 2;
	}
	return 3;
}

/** Answer of the three routes: { status, payload, missingFields, error }. */
interface DraftRouteResponse {
	status: string;
	payload: any;
	missingFields: Array<string> | null;
	error: string | null;
}

async function callDraftRoute(route: string, body: Record<string, unknown>): Promise<DraftRouteResponse> {
	try {
		const res = await fetch(process.env.PRODUCTION_API_URL + "business-dashboard/listings/" + route, {
			method: "POST",
			cache: "no-store",
			headers: { "Content-Type": "text/plain" },
			body: JSON.stringify(body),
		});
		return await res.json();
	} catch {
		return { status: "error", payload: null, missingFields: null, error: "Network error" };
	}
}

/** Saves (or updates) a draft; answers its id, or "" when it could not be saved. */
export async function saveWebPropertyDraft(body: WizardDraftBody): Promise<string> {
	const response = await callDraftRoute("save-property-draft", body);
	return response.status === "success" ? String(response.payload?.property_id || "") : "";
}

/** One of the business's live drafts in the app wizard's body shape, or null. */
export async function fetchWebPropertyDraft(
	businessProfileId: string,
	propertyId: string,
): Promise<WizardDraftBody | null> {
	const response = await callDraftRoute("fetch-property-draft", {
		business_profile_id: businessProfileId,
		property_id: propertyId,
	});
	return response.status === "success" ? response.payload?.draft || null : null;
}

/** Discards one of the business's drafts (a soft delete). */
export async function discardWebPropertyDraft(businessProfileId: string, propertyId: string): Promise<boolean> {
	const response = await callDraftRoute("discard-property-draft", {
		business_profile_id: businessProfileId,
		property_id: propertyId,
	});
	return response.status === "success";
}
